import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useOutletContext } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { WorkspaceViewId } from '../components/CalculatorNavigation';
import WhatIsFinancialRunwayPage from '../pages/Guides/WhatIsFinancialRunwayPage';
import { RouteErrorBoundary, RouteLoadingFallback } from './RouteLoadBoundary';

// Lazy load pages
const Home = lazy(() => import('../pages/Home/Home'));
const EmiCalculator = lazy(() => import('../pages/Calculators/EmiCalculator'));
const SipCalculator = lazy(() => import('../pages/Calculators/SipCalculator'));
const LumpsumCalculator = lazy(() => import('../pages/Calculators/LumpsumCalculator'));
const FdCalculator = lazy(() => import('../pages/Calculators/FdCalculator'));
const RdCalculator = lazy(() => import('../pages/Calculators/RdCalculator'));
const NetWorthDashboard = lazy(() => import('../pages/NetWorth/NetWorthDashboard'));
const Learn = lazy(() => import('../pages/Learn/Learn'));
const About = lazy(() => import('../pages/About/About'));

// Guides
const EmergencyFundMonthsPage = lazy(() => import('../pages/Guides/EmergencyFundMonthsPage'));
const SafeEmiPercentagePage = lazy(() => import('../pages/Guides/SafeEmiPercentagePage'));
const BuildThreeMonthBufferPage = lazy(() => import('../pages/Guides/BuildThreeMonthBufferPage'));

function CalculatorView() {
  const { activeCalculator } = useOutletContext<{ activeCalculator: WorkspaceViewId }>();

  switch (activeCalculator) {
    case 'emi':
      return <EmiCalculator />;
    case 'sip':
      return <SipCalculator />;
    case 'lumpsum':
      return <LumpsumCalculator />;
    case 'fd':
      return <FdCalculator />;
    case 'rd':
      return <RdCalculator />;
    case 'net-worth':
      return <NetWorthDashboard />;
    default:
      return <Home />;
  }
}

const AppRoutes: React.FC = () => {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route index element={<CalculatorView />} />
            <Route path="learn" element={<Learn />} />
            <Route path="learn/financial-runway" element={<WhatIsFinancialRunwayPage />} />
            <Route path="learn/emergency-fund" element={<EmergencyFundMonthsPage />} />
            <Route path="learn/safe-emi-percentage" element={<SafeEmiPercentagePage />} />
            <Route path="learn/build-3-month-buffer" element={<BuildThreeMonthBufferPage />} />
            <Route path="calculators" element={<Navigate to="/" replace />} />
            <Route path="emi" element={<Navigate to="/" replace />} />
            <Route path="sip" element={<Navigate to="/" replace />} />
            <Route path="lumpsum" element={<Navigate to="/" replace />} />
            <Route path="fd" element={<Navigate to="/" replace />} />
            <Route path="rd" element={<Navigate to="/" replace />} />
            <Route path="about" element={<About />} />
            <Route path="net-worth" element={<Navigate to="/" replace />} />

            <Route path="what-is-financial-runway" element={<Navigate to="/learn/financial-runway" replace />} />
            <Route path="how-many-months-emergency-fund" element={<Navigate to="/learn/emergency-fund" replace />} />
            <Route path="what-is-a-safe-emi-percentage" element={<Navigate to="/learn/safe-emi-percentage" replace />} />
            <Route path="how-to-build-3-month-safety-buffer" element={<Navigate to="/learn/build-3-month-buffer" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
};

export default AppRoutes;